import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  BellRing,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  CircleSlash,
  Mail,
  Pause,
  Pencil,
  Play,
  Plus,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/ui/Loader';
import { useAction, useMutation, useQueryState } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { useModuleAccess } from '@/lib/useModuleAccess';
import { fetchExtensions, fetchQueues } from '@/services/api';
import {
  DATE_RANGES,
  DATE_RANGE_LABELS,
  REPORT_KIND_LABELS,
  describeFilters,
  describeTiming,
  isAlertKind,
  scheduleDefaults,
  type DateRangeKey,
  type FilterNames,
  type ReportKind,
  type ScheduledReport,
} from '@/lib/reports/schedule';
import { ScheduleEditor, type ScheduleDraft } from './ScheduleEditor';

/** Reports → Scheduled: presets, then the reports and alerts set up for this account. */

interface ListResult {
  schedules: ScheduledReport[];
  emailReady: boolean;
  customerSelected: boolean;
}

function formatWhen(ms: number) {
  return new Date(ms).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function LastRun({ schedule }: { schedule: ScheduledReport }) {
  if (!schedule.lastRunAtMs || !schedule.lastStatus) {
    return <span className="text-gray-500 dark:text-gray-400">Not sent yet</span>;
  }
  const icon =
    schedule.lastStatus === 'sent' ? (
      <CheckCircle2 className="w-4 h-4 shrink-0 text-primary-600" />
    ) : schedule.lastStatus === 'failed' ? (
      <XCircle className="w-4 h-4 shrink-0 text-red-600" />
    ) : (
      <CircleSlash className="w-4 h-4 shrink-0 text-gray-400" />
    );
  return (
    <span className="inline-flex items-start gap-1.5 min-w-0">
      {icon}
      <span className={schedule.lastStatus === 'failed' ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}>
        {formatWhen(schedule.lastRunAtMs)}: {schedule.lastMessage}
      </span>
    </span>
  );
}

/** "Send now", with a choice of period (e.g. yesterday, when today has no calls yet). */
function SendNowButton({ schedule, onSend, busy }: { schedule: ScheduledReport; onSend: (range?: DateRangeKey) => void; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (isAlertKind(schedule.reportKind)) {
    return (
      <Button size="sm" onClick={() => onSend()} isLoading={busy}>
        <Send className="w-4 h-4" />
        Check now
      </Button>
    );
  }
  return (
    <div ref={ref} className="relative">
      <Button size="sm" onClick={() => setOpen(!open)} isLoading={busy}>
        <Send className="w-4 h-4" />
        Send now
        <ChevronDown className="w-3.5 h-3.5" />
      </Button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-60 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          <p className="px-3 pt-2 pb-1 text-xs text-gray-500 dark:text-gray-400">Send it now, covering:</p>
          {[schedule.dateRange, ...DATE_RANGES.filter((range) => range !== schedule.dateRange)].map((range) => (
            <button
              key={range}
              type="button"
              onClick={() => {
                setOpen(false);
                onSend(range === schedule.dateRange ? undefined : range);
              }}
              className="block w-full px-3 py-2 text-left text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {DATE_RANGE_LABELS[range]}
              {range === schedule.dateRange && <span className="text-gray-500 dark:text-gray-400"> (its usual period)</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ScheduleCard({
  schedule,
  names,
  onEdit,
}: {
  schedule: ScheduledReport;
  names: FilterNames;
  onEdit: () => void;
}) {
  const sendNow = useAction(backendApi.scheduledReports.sendNow);
  const setEnabled = useMutation(backendApi.scheduledReports.setEnabled);
  const remove = useMutation(backendApi.scheduledReports.remove);
  const [sending, setSending] = useState(false);
  const alert = isAlertKind(schedule.reportKind);

  const send = async (dateRange?: DateRangeKey) => {
    setSending(true);
    try {
      const result: { ok: boolean; status: string; message: string } = await sendNow({ id: schedule._id, dateRange });
      if (!result.ok) toast.error(result.message);
      else if (result.status === 'nothing') toast(result.message, { icon: '✅' });
      else toast.success(result.message);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not send');
    } finally {
      setSending(false);
    }
  };

  const toggle = async () => {
    try {
      await setEnabled({ id: schedule._id, enabled: !schedule.enabled });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not change it');
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${schedule.name}"? Its emails stop.`)) return;
    try {
      await remove({ id: schedule._id });
      toast.success('Deleted');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not delete');
    }
  };

  const filters = describeFilters(schedule.reportKind, schedule.filters, names);
  return (
    <div
      className={`rounded-2xl border p-4 sm:p-5 ${
        schedule.enabled ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800' : 'border-dashed border-gray-300 dark:border-gray-600 bg-panel dark:bg-gray-900/40'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {alert ? <BellRing className="w-4 h-4 text-amber-600" /> : <CalendarClock className="w-4 h-4 text-primary-600" />}
            <h4 className="font-semibold text-gray-900 dark:text-white">{schedule.name}</h4>
            {!schedule.enabled && (
              <span className="rounded-full bg-gray-200 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300">Paused</span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            {alert ? filters.join(' · ') : `${REPORT_KIND_LABELS[schedule.reportKind]} · ${filters.join(' · ')}`}
          </p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            {describeTiming(schedule)}
            {!alert && ` · covering ${DATE_RANGE_LABELS[schedule.dateRange].toLowerCase()}`}
            {!alert && schedule.formats.length > 0 && ` · ${schedule.formats.map((f) => f.toUpperCase()).join(' + ')}`}
          </p>
          <p className="mt-1 flex items-start gap-1.5 text-sm text-gray-600 dark:text-gray-300">
            <Mail className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
            <span className="break-all">{schedule.recipients.join(', ')}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SendNowButton schedule={schedule} onSend={send} busy={sending} />
          <Button size="sm" variant="secondary" onClick={onEdit} title="Edit">
            <Pencil className="w-4 h-4" />
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={toggle} title={schedule.enabled ? 'Pause' : 'Resume'}>
            {schedule.enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {schedule.enabled ? 'Pause' : 'Resume'}
          </Button>
          <Button size="sm" variant="ghost" onClick={del} title="Delete" className="text-red-600 dark:text-red-400">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-2 border-t border-gray-100 dark:border-gray-700 pt-3 text-sm">
        <LastRun schedule={schedule} />
        <span className="text-gray-500 dark:text-gray-400 md:text-right">
          {schedule.enabled && schedule.nextRunAtMs ? `Next ${alert ? 'check' : 'send'}: ${formatWhen(schedule.nextRunAtMs)}` : 'Not running'}
          {schedule.ownerName ? ` · set up by ${schedule.ownerName}` : ''}
        </span>
      </div>
    </div>
  );
}

const PRESETS: Array<{ kind: ReportKind; title: string; text: string }> = [
  {
    kind: 'activity',
    title: 'Daily Activity',
    text: 'Every day at 18:00: calls received, made and missed for all extensions, the missed calls and whether they were returned, and each extension’s calls made with minutes and cost.',
  },
  {
    kind: 'missedAlert',
    title: 'Unreturned missed call alert',
    text: 'Checks every hour from 07:00 to 18:00 and emails the missed calls nobody has returned within 15 minutes. Nothing is sent when there are none.',
  },
];

export function ScheduledReports() {
  const { session } = useModuleAccess('reporting');
  const { data, error, isLoading } = useQueryState<ListResult>(backendApi.scheduledReports.list, {});
  const [editing, setEditing] = useState<{ draft: ScheduleDraft; id?: string; allowKindChange?: boolean } | null>(null);
  const [names, setNames] = useState<FilterNames>({});
  const schedules = useMemo(() => data?.schedules ?? [], [data]);

  // Names for the extension and queue numbers in the filters
  useEffect(() => {
    const needsExtensions = schedules.some((s) => ['summary', 'detail', 'costs'].includes(s.reportKind));
    const needsQueues = schedules.some((s) => s.reportKind === 'queueMissed' || s.reportKind === 'failover');
    if (needsExtensions && !names.extensions) {
      fetchExtensions()
        .then((list) => setNames((n) => ({ ...n, extensions: new Map(list.map((e) => [e.number, e.display_name || e.username || ''])) })))
        .catch(() => undefined);
    }
    if (needsQueues && !names.queues) {
      fetchQueues()
        .then((list) => setNames((n) => ({ ...n, queues: new Map(list.map((q) => [q.id, q.name || q.number || q.id])) })))
        .catch(() => undefined);
    }
  }, [schedules, names.extensions, names.queues]);

  const myEmail = session?.user.email ? [session.user.email] : [];
  const startNew = (kind: ReportKind, allowKindChange = false) => {
    const defaults = scheduleDefaults(kind);
    setEditing({ draft: { ...defaults, filters: defaults.filters as Record<string, unknown>, recipients: myEmail }, allowKindChange });
  };
  const edit = (schedule: ScheduledReport) =>
    setEditing({
      id: schedule._id,
      draft: {
        ...schedule,
        filters: schedule.filters as unknown as Record<string, unknown>,
      },
    });

  const reports = schedules.filter((s) => !isAlertKind(s.reportKind));
  const alerts = schedules.filter((s) => isAlertKind(s.reportKind));

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
              <CalendarClock className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Scheduled reports and alerts</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Reports emailed on a schedule, and alerts that email only when something needs attention. Times are South African time.
              </p>
            </div>
          </div>
          <Button variant="secondary" onClick={() => startNew('costs', true)}>
            <Plus className="w-4 h-4" />
            Other report
          </Button>
        </div>

        {data && !data.emailReady && (
          <div className="mb-5 flex items-start gap-2 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-100">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            Email isn’t set up on the server yet. Schedules are saved, but nothing is sent until it is.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {PRESETS.map((preset) => (
            <div key={preset.kind} className="flex flex-col justify-between gap-3 rounded-2xl bg-panel dark:bg-gray-900/60 p-4">
              <div>
                <h3 className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
                  {isAlertKind(preset.kind) ? <BellRing className="w-4 h-4 text-amber-600" /> : <CalendarClock className="w-4 h-4 text-primary-600" />}
                  {preset.title}
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{preset.text}</p>
              </div>
              <div>
                <Button size="sm" onClick={() => startNew(preset.kind)}>
                  <Plus className="w-4 h-4" />
                  Set up
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {isLoading && !data ? (
        <Card>
          <Loader text="Loading schedules..." />
        </Card>
      ) : error ? (
        <Card>
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </Card>
      ) : data && !data.customerSelected ? (
        <Card>
          <p className="text-sm text-gray-600 dark:text-gray-300">Select a customer to see their scheduled reports.</p>
        </Card>
      ) : (
        <>
          {[
            { title: 'Scheduled reports', list: reports, empty: 'No scheduled reports yet. Set up Daily Activity above, or use Schedule on any report.' },
            { title: 'Alerts', list: alerts, empty: 'No alerts yet. Set up the unreturned missed call alert above.' },
          ].map((group) => (
            <Card key={group.title}>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{group.title}</h3>
              {group.list.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{group.empty}</p>
              ) : (
                <div className="space-y-3">
                  {group.list.map((schedule) => (
                    <ScheduleCard key={schedule._id} schedule={schedule} names={names} onEdit={() => edit(schedule)} />
                  ))}
                </div>
              )}
            </Card>
          ))}
        </>
      )}

      {editing && (
        <ScheduleEditor
          isOpen
          onClose={() => setEditing(null)}
          initial={editing.draft}
          id={editing.id}
          allowKindChange={editing.allowKindChange}
        />
      )}
    </div>
  );
}
